import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Users,
    Stethoscope,
    Package,
    ShoppingBag,
    CalendarClock,
    Wallet,
    Check,
    X,
    AlertTriangle,
    MessageCircle,
    Phone,
    Eye,
    UserPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Stars, Money } from "@/components/ui-veda/Primitives";

function Tile({ icon: Icon, label, value, tone = "muted", testid }) {
    const toneMap = {
        brand: "bg-veda-brand/10 text-veda-brand",
        success: "bg-veda-success/10 text-veda-success",
        warning: "bg-veda-warning/10 text-veda-warning",
        error: "bg-veda-error/10 text-veda-error",
        muted: "bg-veda-surface2 text-veda-onSurface",
    };
    return (
        <div
            data-testid={testid}
            className="border border-veda-border bg-veda-surface p-4"
        >
            <div className="flex items-center gap-2">
                <span
                    className={`grid h-9 w-9 place-items-center ${toneMap[tone]}`}
                >
                    <Icon size={16} />
                </span>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    {label}
                </p>
            </div>
            <p className="font-display mt-3 text-3xl text-veda-onSurface">
                {value}
            </p>
        </div>
    );
}

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [products, setProducts] = useState([]);
    const [pending, setPending] = useState([]);
    const [orders, setOrders] = useState([]);
    const [visitors, setVisitors] = useState(null);
    const [visitorRange, setVisitorRange] = useState(30);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        (async () => {
            const [s, a, pAll, pdocs, o] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/analytics"),
                api.get("/products"),
                api.get("/doctors", { params: { status: "pending" } }),
                api.get("/orders"),
            ]);
            setStats(s.data);
            setAnalytics(a.data);
            setProducts(pAll.data);
            setPending(pdocs.data);
            setOrders(o.data);
        })();
    }, [refreshKey]);

    useEffect(() => {
        api
            .get("/admin/visitors", { params: { days: visitorRange } })
            .then((r) => setVisitors(r.data))
            .catch(() => setVisitors(null));
    }, [visitorRange, refreshKey]);

    const lowStock = useMemo(
        () => products.filter((p) => (p.stock ?? 0) < 5),
        [products],
    );
    const revenueMax = useMemo(
        () => Math.max(1, ...(analytics?.revenue_7d?.map((b) => b.revenue) || [0])),
        [analytics],
    );
    const visitorMax = useMemo(
        () => Math.max(1, ...(visitors?.buckets?.map((b) => b.unique_visitors) || [0])),
        [visitors],
    );

    async function approve(did) {
        try {
            await api.put(`/doctors/${did}`, { status: "approved" });
            toast.success("Approved.");
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Could not approve.");
        }
    }
    async function reject(did) {
        try {
            await api.put(`/doctors/${did}`, { status: "rejected" });
            toast.success("Marked as rejected.");
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Could not reject.");
        }
    }

    return (
        <div data-testid={TID.adminOverview} className="flex flex-col gap-8">
            {/* Metric tiles — 2 cols mobile → 3 tablet → 6 desktop */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                <Tile icon={Users} label="Customers" value={stats?.customers ?? "—"} tone="brand" />
                <Tile icon={Stethoscope} label="Doctors" value={stats?.doctors ?? "—"} tone="success" />
                <Tile icon={Package} label="Products" value={stats?.products ?? "—"} />
                <Tile icon={ShoppingBag} label="Orders" value={stats?.orders ?? "—"} tone="warning" />
                <Tile icon={CalendarClock} label="Consultations" value={stats?.consultations ?? "—"} />
                <Tile icon={Wallet} label="Revenue" value={<Money value={stats?.revenue || 0} />} tone="success" />
            </div>

            {/* Row 1: Pending approvals */}
            <div className="grid gap-6">
                <div
                    data-testid={TID.pendingDoctorsBlock}
                    className="border border-veda-border bg-veda-surface p-5"
                >
                    <div className="flex items-center justify-between">
                        <p className="font-display text-xl text-veda-onSurface">
                            Awaiting approval
                        </p>
                        <Link
                            to="/admin/doctors"
                            className="text-xs font-medium text-veda-brand underline underline-offset-2"
                        >
                            Manage
                        </Link>
                    </div>
                    {pending.length === 0 ? (
                        <p className="mt-3 text-sm text-veda-muted">
                            No new applications.
                        </p>
                    ) : (
                        <ul className="mt-3 divide-y divide-veda-divider">
                            {pending.map((d) => (
                                <li
                                    key={d.doctor_id}
                                    className="flex items-center gap-3 py-3"
                                >
                                    <img
                                        src={d.avatar_url}
                                        alt=""
                                        className="h-10 w-10 object-cover"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-veda-onSurface">
                                            {d.name}
                                        </p>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                            {d.specialty} · {d.experience_years}y
                                        </p>
                                    </div>
                                    <button
                                        data-testid={TID.approve(d.doctor_id)}
                                        onClick={() => approve(d.doctor_id)}
                                        className="inline-flex items-center gap-1 border border-veda-success bg-veda-success px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                                    >
                                        <Check size={11} /> Approve
                                    </button>
                                    <button
                                        data-testid={TID.reject(d.doctor_id)}
                                        onClick={() => reject(d.doctor_id)}
                                        className="inline-flex items-center gap-1 border border-veda-error px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error"
                                    >
                                        <X size={11} /> Reject
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Row 2: Low stock + Recent orders */}
            <div className="grid gap-6 lg:grid-cols-2">
                <div
                    data-testid={TID.lowStockBlock}
                    className="border border-veda-border bg-veda-surface p-5"
                >
                    <div className="flex items-center justify-between">
                        <p className="font-display text-xl text-veda-onSurface">
                            Low stock
                        </p>
                        <Link
                            to="/admin/products"
                            className="text-xs font-medium text-veda-brand underline underline-offset-2"
                        >
                            Restock
                        </Link>
                    </div>
                    {lowStock.length === 0 ? (
                        <p className="mt-3 text-sm text-veda-muted">
                            Everything is well-stocked.
                        </p>
                    ) : (
                        <ul className="mt-3 divide-y divide-veda-divider">
                            {lowStock.map((p) => (
                                <li key={p.product_id} className="flex items-center gap-3 py-2">
                                    <span className="grid h-7 w-7 place-items-center bg-veda-warning/10 text-veda-warning">
                                        <AlertTriangle size={12} />
                                    </span>
                                    <p className="flex-1 text-sm text-veda-onSurface">
                                        {p.name}
                                    </p>
                                    <span className="text-xs font-semibold text-veda-warning">
                                        {p.stock} left
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border border-veda-border bg-veda-surface p-5">
                    <div className="flex items-center justify-between">
                        <p className="font-display text-xl text-veda-onSurface">
                            Recent orders
                        </p>
                        <Link
                            to="/admin/orders"
                            className="text-xs font-medium text-veda-brand underline underline-offset-2"
                        >
                            View all
                        </Link>
                    </div>
                    {orders.length === 0 ? (
                        <p className="mt-3 text-sm text-veda-muted">
                            No orders yet.
                        </p>
                    ) : (
                        <ul className="mt-3 divide-y divide-veda-divider">
                            {orders.slice(0, 6).map((o) => (
                                <li
                                    key={o.order_id}
                                    className="flex items-center justify-between py-2"
                                >
                                    <div>
                                        <p className="text-xs font-semibold text-veda-onSurface">
                                            #{o.order_id.slice(-6)} · {o.customer_name}
                                        </p>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                            {o.status}
                                        </p>
                                    </div>
                                    <p className="text-sm font-semibold text-veda-onSurface">
                                        <Money value={o.total} />
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Insights header */}
            <div className="mt-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Insights
                </p>
                <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                    How the practice is doing
                </h2>
            </div>

            {/* Visitors — day-wise */}
            <div
                data-testid={TID.visitorsBlock}
                className="border border-veda-border bg-veda-surface p-5"
            >
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Website visitors · last {visitorRange} days
                        </p>
                        <div className="mt-2 flex flex-wrap items-baseline gap-5">
                            <div>
                                <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                    <UserPlus size={11} /> Unique visitors
                                </p>
                                <p
                                    data-testid={TID.visitorsTotalUnique}
                                    className="font-display text-3xl text-veda-onSurface"
                                >
                                    {visitors?.total_unique_visitors ?? "—"}
                                </p>
                            </div>
                            <div>
                                <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                    <Eye size={11} /> Pageviews
                                </p>
                                <p
                                    data-testid={TID.visitorsTotalPageviews}
                                    className="font-display text-3xl text-veda-onSurface"
                                >
                                    {visitors?.total_pageviews ?? "—"}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-1">
                        {[7, 30, 90].map((n) => (
                            <button
                                key={n}
                                onClick={() => setVisitorRange(n)}
                                data-testid={TID.visitorsRangeBtn(n)}
                                className={`rounded-pill border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                    visitorRange === n
                                        ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                        : "border-veda-border bg-veda-surface text-veda-onSurface"
                                }`}
                            >
                                {n}d
                            </button>
                        ))}
                    </div>
                </div>

                {visitors?.buckets?.length ? (
                    <div className="no-scrollbar mt-6 overflow-x-auto">
                        <div
                            className="flex h-40 items-end gap-1 md:h-52"
                            style={{
                                minWidth: `${Math.max(visitors.buckets.length * 22, 100)}px`,
                            }}
                        >
                            {visitors.buckets.map((b) => {
                                const h = Math.max(
                                    2,
                                    Math.round(
                                        (b.unique_visitors / visitorMax) * 100,
                                    ),
                                );
                                return (
                                    <div
                                        key={b.date}
                                        data-testid={TID.visitorBar(b.date)}
                                        title={`${b.label}: ${b.unique_visitors} unique · ${b.pageviews} views`}
                                        className="flex h-full flex-1 items-end"
                                    >
                                        <div
                                            className="w-full bg-veda-brand transition-all"
                                            style={{ height: `${h}%` }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {/* X-axis labels — sparse for readability */}
                        <div
                            className="mt-2 flex gap-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-veda-muted"
                            style={{
                                minWidth: `${Math.max(visitors.buckets.length * 22, 100)}px`,
                            }}
                        >
                            {visitors.buckets.map((b, i) => {
                                const step = Math.max(
                                    1,
                                    Math.round(visitors.buckets.length / 8),
                                );
                                const show =
                                    i === 0 ||
                                    i === visitors.buckets.length - 1 ||
                                    i % step === 0;
                                return (
                                    <span
                                        key={b.date}
                                        className="flex-1 text-center"
                                    >
                                        {show ? b.label : ""}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <p className="mt-6 text-sm text-veda-muted">
                        No visits recorded yet. Once traffic starts, this chart
                        will populate automatically.
                    </p>
                )}
            </div>

            {/* Revenue chart — full width */}
            <div
                data-testid={TID.revenueChart}
                className="border border-veda-border bg-veda-surface p-5"
            >
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                    Revenue · last 7 days
                </p>
                <div className="mt-4 flex h-40 items-end gap-2 md:h-56">
                    {(analytics?.revenue_7d || []).map((b) => {
                        const h = Math.max(2, Math.round((b.revenue / revenueMax) * 100));
                        return (
                            <div
                                key={b.date}
                                className="flex flex-1 flex-col items-center gap-1"
                                title={`${b.label}: ₹${b.revenue}`}
                            >
                                <div className="flex h-full w-full items-end">
                                    <div
                                        className="w-full bg-veda-brand transition-all"
                                        style={{ height: `${h}%` }}
                                    />
                                </div>
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                    {b.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Row 3: Top products + Top doctors + Mix */}
            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <div
                    data-testid={TID.topProductsBlock}
                    className="border border-veda-border bg-veda-surface p-5"
                >
                    <p className="font-display text-xl text-veda-onSurface">
                        Top products
                    </p>
                    {analytics?.top_products?.length ? (
                        <ul className="mt-3 flex flex-col gap-3">
                            {analytics.top_products.map((p, idx) => {
                                const max = analytics.top_products[0].qty || 1;
                                return (
                                    <li key={p.product_id}>
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="font-semibold text-veda-onSurface">
                                                {idx + 1}. {p.name}
                                            </span>
                                            <span className="text-veda-muted">
                                                {p.qty} sold · <Money value={p.revenue} />
                                            </span>
                                        </div>
                                        <div className="mt-1 h-1.5 bg-veda-surface2">
                                            <div
                                                className="h-full bg-veda-brand"
                                                style={{ width: `${(p.qty / max) * 100}%` }}
                                            />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="mt-3 text-sm text-veda-muted">
                            No sales data yet.
                        </p>
                    )}
                </div>

                <div
                    data-testid={TID.topDoctorsBlock}
                    className="border border-veda-border bg-veda-surface p-5"
                >
                    <p className="font-display text-xl text-veda-onSurface">
                        Best-rated Vaidyas
                    </p>
                    {analytics?.top_doctors?.length ? (
                        <ul className="mt-3 divide-y divide-veda-divider">
                            {analytics.top_doctors.map((d) => (
                                <li
                                    key={d.doctor_id}
                                    className="flex items-center gap-3 py-2"
                                >
                                    <img
                                        src={d.avatar_url}
                                        alt=""
                                        className="h-10 w-10 object-cover"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-veda-onSurface">
                                            {d.name}
                                        </p>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                            {d.specialty}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <Stars value={d.rating} size={10} />
                                        <span className="text-[10px] text-veda-muted">
                                            {d.rating_count || 0} reviews
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-3 text-sm text-veda-muted">
                            No reviews yet.
                        </p>
                    )}
                </div>

                <div
                    data-testid={TID.mixBlock}
                    className="border border-veda-border bg-veda-surface p-5 lg:col-span-2 xl:col-span-1"
                >
                    <p className="font-display text-xl text-veda-onSurface">
                        Consultation mix
                    </p>
                    <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                        <div className="border border-veda-border bg-veda-surface p-3">
                            <MessageCircle
                                size={16}
                                className="mx-auto text-veda-brand"
                            />
                            <p className="mt-2 font-display text-2xl text-veda-onSurface">
                                {analytics?.consultation_mix?.chat ?? 0}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Chat
                            </p>
                        </div>
                        <div className="border border-veda-border bg-veda-surface p-3">
                            <Phone size={16} className="mx-auto text-veda-brand" />
                            <p className="mt-2 font-display text-2xl text-veda-onSurface">
                                {analytics?.consultation_mix?.call ?? 0}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Call
                            </p>
                        </div>
                        <div className="border border-veda-border bg-veda-surface p-3">
                            <CalendarClock
                                size={16}
                                className="mx-auto text-veda-brand"
                            />
                            <p className="mt-2 font-display text-2xl text-veda-onSurface">
                                {analytics?.consultation_mix?.total ?? 0}
                            </p>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Total
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
